import { HUB_MAPS, HubMapId, DEFAULT_HUB_MAP_ID } from '../data/hub-map.config';
import { useHubStore } from '../store/hubSlice';
import { HUB_PHYSICS } from '../data/hub.config';
import {
  HubAabb,
  HubDoorPlacement,
  HubNpcPlacement,
  HubSpawnPoint,
  DreamBubbleConfig,
} from '../types/hub.types';
import { DirectionEnum } from '../types/enums';
import { useGameStore } from '../store/gameSlice';
import { fadeTransition } from './TransitionService';
import { rollShopStock } from './ShopService';
import { cinematicService } from './CinematicService';
import { useUnlockedDungeonsStore } from '../store/unlockedDungeonsSlice';
import { useNpcStore } from '../store/npcSlice';
import { useHubProgressStore } from '../store/hubProgressSlice';
import { netService } from './NetService';
import { RETURN_TO_HUB } from '../net/sessionEvents';

// Spawn ids that mean "a run just ended" — used to refill the rolling shop.
const RUN_END_SPAWN_IDS = new Set(['from-dungeon-exit', 'from-defeat']);

const HUB_SESSION_KEY = 'usagi_hub';
const HUB_SESSION_VERSION = 1;

class HubService {
  private collisionMask: Uint8Array | null = null;
  private maskWidth = 0;
  private maskHeight = 0;
  private loadPromise: Promise<void> | null = null;

  // Hot-path runtime state, deliberately kept out of the React store.
  playerX = 0;
  playerY = 0;
  vx = 0;
  vy = 0;
  facing: DirectionEnum = DirectionEnum.TOP;
  bubbleProgress = 1;
  cinematicWalking = false;
  wakingUp = false;
  cameraOverride: { x: number; y: number } | null = null;
  cinematicZoom = 1;
  private pendingArrivalSpawnId: string | null = null;

  private currentMapId: HubMapId = DEFAULT_HUB_MAP_ID;
  private switching = false;

  private get map() {
    return HUB_MAPS[this.currentMapId];
  }

  get mapId(): HubMapId {
    return this.currentMapId;
  }

  // -- Session persistence (which hub map the player is on) ----------------
  //
  // Hub progression (chest, loadout, talents, purchases, spell catalogue) is
  // already persisted per-mutation by its own Zustand slices. The only extra
  // session state is the current map id; the player position is intentionally
  // not saved (resume respawns at the map's `default` spawn).

  private persistSession(): void {
    try {
      localStorage.setItem(
        HUB_SESSION_KEY,
        JSON.stringify({
          version: HUB_SESSION_VERSION,
          hubMapId: this.currentMapId,
        }),
      );
    } catch {
      // localStorage may be unavailable (private mode / quota) — non-critical
    }
  }

  /**
   * Restores the persisted hub map id at boot, before any hub entry. Falls
   * back to the default map on absent / invalid / version-mismatched data.
   */
  restoreSession(): void {
    let id: HubMapId = DEFAULT_HUB_MAP_ID;
    try {
      const raw = localStorage.getItem(HUB_SESSION_KEY);
      if (raw) {
        const parsed = JSON.parse(raw) as {
          version?: number;
          hubMapId?: string;
        };
        if (
          parsed.version === HUB_SESSION_VERSION &&
          parsed.hubMapId &&
          parsed.hubMapId in HUB_MAPS
        ) {
          id = parsed.hubMapId as HubMapId;
        }
      }
    } catch {
      // Corrupt session — fall back to default.
    }
    this.currentMapId = id;
    useHubStore.getState().setCurrentHubMapId(id);
  }

  resetPlayer(x: number, y: number, facing: DirectionEnum): void {
    this.playerX = x;
    this.playerY = y;
    this.vx = 0;
    this.vy = 0;
    this.facing = facing;
    this.cinematicWalking = false;
    this.wakingUp = false;
    this.cameraOverride = null;
    this.cinematicZoom = 1;
  }

  /** `initial` before D1, `day` after. Night stays reachable via switchMap. */
  private resolveEntryMapId(): HubMapId {
    return useUnlockedDungeonsStore.getState().isCompleted('clear-forest')
      ? 'day'
      : 'initial';
  }

  private applyEntryMap(mapId: HubMapId): void {
    if (mapId === this.currentMapId) {
      return;
    }
    this.currentMapId = mapId;
    useHubStore.getState().setCurrentHubMapId(mapId);
    this.collisionMask = null;
    this.loadPromise = null;
    this.persistSession();
  }

  // Use when arriving fresh in the hub (MainMenu, run exit, defeat): this
  // repositions the player at the named spawn. For returns from an already-open
  // screen (chest, forge, etc.), keep `setGameplay('hub')` which preserves the
  // position.
  async enterHub(spawnId: string): Promise<void> {
    const isRunReturn = RUN_END_SPAWN_IDS.has(spawnId);
    if (isRunReturn && netService.isHost() && netService.session) {
      netService.session.setPhase('hub');
      netService.session.broadcast('reliable', RETURN_TO_HUB);
    }
    this.applyEntryMap(this.resolveEntryMapId());
    const sp = this.getSpawnPoint(spawnId);
    const pendingD1Reveal =
      isRunReturn &&
      useUnlockedDungeonsStore.getState().isCompleted('clear-forest') &&
      !useHubProgressStore.getState().d1RevealPlayed;
    this.bubbleProgress = pendingD1Reveal ? 0 : 1;
    this.pendingArrivalSpawnId = isRunReturn ? spawnId : null;
    if (isRunReturn) {
      rollShopStock();
    }
    await fadeTransition(async () => {
      // Hold black until end-of-run unlock showcases finish (they render above
      // the fade), so they don't fight the dungeon teardown or the hub intro.
      await cinematicService.awaitShowcasesDrained();
      this.resetPlayer(sp.x, sp.y, sp.facing);
      useGameStore.getState().setGameplay('hub');
    });
    this.persistSession();
  }

  /** Returns and clears the pending run-return arrival, if any. */
  consumePendingArrival(): string | null {
    const id = this.pendingArrivalSpawnId;
    this.pendingArrivalSpawnId = null;
    return id;
  }

  /**
   * Switches to another hub map (day ↔ night, or a linked location via a door)
   * under a fade in/out: the map id flips and the new collision mask reloads
   * while the screen is black, then the player respawns at `spawnId` of the new
   * map. Night is effectively "another hub". Re-entrant calls are ignored
   * (guards against a door firing every frame before the fade hides the map).
   */
  async switchMap(id: HubMapId, spawnId = 'default'): Promise<void> {
    if (this.switching || id === this.currentMapId) {
      return;
    }
    this.switching = true;
    try {
      await fadeTransition(async () => {
        this.currentMapId = id;
        useHubStore.getState().setCurrentHubMapId(id);
        this.persistSession();
        await this.reloadMap();
        const sp = this.getSpawnPoint(spawnId);
        this.resetPlayer(sp.x, sp.y, sp.facing);
      });
    } finally {
      this.switching = false;
    }
  }

  /** Convenience: toggle the bedroom day ↔ night hub. */
  toggleDayNight(): Promise<void> {
    return this.switchMap(this.currentMapId === 'day' ? 'night' : 'day');
  }

  /**
   * Loads the host's current hub map on a client (shared hub). Like switchMap
   * but never persists (the client mirrors the host, it doesn't own a session)
   * and always respawns at `default`. No-op if already on that map. Re-entrant
   * calls are ignored via the `switching` guard.
   */
  async applyRemoteMap(id: HubMapId): Promise<void> {
    if (this.switching || id === this.currentMapId) {
      return;
    }
    this.switching = true;
    try {
      await fadeTransition(async () => {
        this.currentMapId = id;
        useHubStore.getState().setCurrentHubMapId(id);
        await this.reloadMap();
        const sp = this.getSpawnPoint('default');
        this.resetPlayer(sp.x, sp.y, sp.facing);
      });
    } finally {
      this.switching = false;
    }
  }

  private async reloadMap(): Promise<void> {
    useHubStore.getState().setMapLoaded(false);
    this.collisionMask = null;
    this.loadPromise = null;
    await this.loadMap();
    useHubStore.getState().setMapLoaded(true);
  }

  get isLoaded(): boolean {
    return this.collisionMask !== null;
  }

  get width(): number {
    return this.map.width;
  }

  get height(): number {
    return this.map.height;
  }

  get currentDreamBubble(): DreamBubbleConfig | undefined {
    return this.map.dreamBubble;
  }

  get npcs(): readonly HubNpcPlacement[] {
    return this.map.npcs;
  }

  get bed(): { x: number; y: number } {
    return this.map.bed;
  }

  loadMap(): Promise<void> {
    if (this.loadPromise) {
      return this.loadPromise;
    }
    this.loadPromise = this.doLoad().catch((err) => {
      // Reset so a retry is possible if the asset arrives later.
      this.loadPromise = null;
      throw err;
    });
    return this.loadPromise;
  }

  private async doLoad(): Promise<void> {
    const img = await loadImage(this.map.collisionMaskUrl);
    const canvas = document.createElement('canvas');
    canvas.width = img.width;
    canvas.height = img.height;
    const ctx = canvas.getContext('2d');
    if (!ctx) {
      throw new Error('HubService: cannot acquire 2D context');
    }
    ctx.drawImage(img, 0, 0);
    const { data } = ctx.getImageData(0, 0, img.width, img.height);
    const mask = new Uint8Array(img.width * img.height);
    for (let i = 0, p = 0; i < data.length; i += 4, p++) {
      // Solid when the pixel is markedly red-dominant.
      if (data[i] > 128 && data[i + 3] > 64) {
        mask[p] = 1;
      }
    }
    this.collisionMask = mask;
    this.maskWidth = img.width;
    this.maskHeight = img.height;
  }

  // Out-of-map coordinates count as solid.
  isSolidAt(x: number, y: number): boolean {
    if (!this.collisionMask) {
      return false;
    }
    const ix = x | 0;
    const iy = y | 0;
    if (ix < 0 || iy < 0 || ix >= this.maskWidth || iy >= this.maskHeight) {
      return true;
    }
    return this.collisionMask[iy * this.maskWidth + ix] === 1;
  }

  // Samples 5 points (4 corners + center) — sufficient for AABBs ≤ 16 px wide.
  isSolidAabb(cx: number, cy: number, aabb: HubAabb): boolean {
    const { halfWidth: hw, halfHeight: hh } = aabb;
    return (
      this.isSolidAt(cx - hw, cy - hh) ||
      this.isSolidAt(cx + hw, cy - hh) ||
      this.isSolidAt(cx - hw, cy + hh) ||
      this.isSolidAt(cx + hw, cy + hh) ||
      this.isSolidAt(cx, cy)
    );
  }

  aabbOverlap(
    aCx: number,
    aCy: number,
    a: HubAabb,
    bCx: number,
    bCy: number,
    b: HubAabb,
  ): boolean {
    return (
      Math.abs(aCx - bCx) < a.halfWidth + b.halfWidth &&
      Math.abs(aCy - bCy) < a.halfHeight + b.halfHeight
    );
  }

  getBlockingNpc(
    cx: number,
    cy: number,
    playerAabb: HubAabb,
  ): HubNpcPlacement | null {
    for (const npc of this.map.npcs) {
      if (!npc.collision) {
        continue;
      }
      if (this.aabbOverlap(cx, cy, playerAabb, npc.x, npc.y, npc.collision)) {
        return npc;
      }
    }
    return null;
  }

  // No orientation filter: diagonal movement is free, so an NPC at any angle
  // qualifies as long as it is within `interactionRange` (center-to-center).
  getInteractableNpc(playerX: number, playerY: number): HubNpcPlacement | null {
    const range = HUB_PHYSICS.interactionRange;
    let best: HubNpcPlacement | null = null;
    let bestDistSq = range * range;
    for (const npc of this.map.npcs) {
      if (!useNpcStore.getState().isUnlocked(npc.npcId)) {
        continue;
      }
      const dx = npc.x - playerX;
      const dy = npc.y - playerY;
      const distSq = dx * dx + dy * dy;
      if (distSq < bestDistSq) {
        bestDistSq = distSq;
        best = npc;
      }
    }
    return best;
  }

  get doors(): readonly HubDoorPlacement[] {
    return this.map.doors ?? [];
  }

  /** Door whose footprint the player currently overlaps (collision trigger). */
  getCollisionDoor(
    cx: number,
    cy: number,
    playerAabb: HubAabb,
  ): HubDoorPlacement | null {
    for (const door of this.doors) {
      if (door.trigger !== 'collision') {
        continue;
      }
      if (
        this.aabbOverlap(cx, cy, playerAabb, door.x, door.y, door.collision)
      ) {
        return door;
      }
    }
    return null;
  }

  /** Nearest interact-trigger door within `interactionRange`, else null. */
  getInteractableDoor(
    playerX: number,
    playerY: number,
  ): HubDoorPlacement | null {
    const range = HUB_PHYSICS.interactionRange;
    let best: HubDoorPlacement | null = null;
    let bestDistSq = range * range;
    for (const door of this.doors) {
      if (door.trigger !== 'interact') {
        continue;
      }
      const dx = door.x - playerX;
      const dy = door.y - playerY;
      const distSq = dx * dx + dy * dy;
      if (distSq < bestDistSq) {
        bestDistSq = distSq;
        best = door;
      }
    }
    return best;
  }

  getSpawnPoint(id: string): HubSpawnPoint {
    const sp = this.map.spawnPoints[id];
    if (sp) {
      return sp;
    }
    // Unknown id falls back to 'default': avoids a crash and lets a spawn
    // point be added without breaking older callers.
    return (
      this.map.spawnPoints.default ?? { x: 0, y: 0, facing: DirectionEnum.TOP }
    );
  }
}

function loadImage(url: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error(`HubService: failed to load ${url}`));
    img.src = url;
  });
}

export const hubService = new HubService();
